import { z } from 'zod';
import { TimestampSchema } from '../primitives.js';
import { findOffer } from './offer.js';
import { subscriptionKind } from './policy.js';
/**
 * Where a business's included minutes stand (doc 61 R04), decided by the
 * SERVER from what it has verified — never inferred by the browser from
 * "subscribed but no balance". Each state has a different honest message:
 *
 * - `live`              — a paid period's allowance is spendable now;
 * - `scheduled`         — a paid period starts later (`nextAt`);
 * - `checking`          — subscribed, the period's invoice not seen yet;
 * - `awaiting_payment`  — the period's invoice is not paid (or past due);
 * - `next_renewal`      — this period does not qualify (it began before the
 *                         offer, or it is a trial / zero-value period); the
 *                         next paid renewal does (`nextAt`);
 * - `unsupported_price` — the subscription is on a price that never includes
 *                         minutes (a legacy plan): no grant will ever arrive;
 * - `review`            — refunded, disputed or otherwise held for support;
 * - `none`              — no offer, or no subscription to grant from.
 */
export const IncludedPreparation = z.enum([
    'none',
    'live',
    'scheduled',
    'checking',
    'awaiting_payment',
    'next_renewal',
    'unsupported_price',
    'review',
]);
/**
 * The last verdict on the business's latest subscription invoice,
 * `billingState/allowanceCheck` (server-only). Written by the webhook, the
 * reconciler and the owner's "check again"; read by the billing overview.
 */
export const AllowanceCheckSchema = z.object({
    /** The processor's outcome string, e.g. `allowance:unpaid`. */
    outcome: z.string(),
    invoiceId: z.string().nullable().default(null),
    at: TimestampSchema,
});
/** Outcomes that say nothing about the current period (no record kept). */
export function isAllowanceCheckWorthKeeping(outcome) {
    return outcome.startsWith('allowance:') && !['allowance:no_business', 'allowance:not_prepaid', 'allowance:no_offer'].includes(outcome);
}
function fromOutcome(outcome) {
    if (outcome === 'allowance:unpaid' || outcome === 'allowance:historical')
        return 'awaiting_payment';
    if (outcome.startsWith('allowance:review:') || outcome.startsWith('allowance:reversed_before_grant') || outcome === 'allowance:blocked_by_reversal' || outcome === 'allowance:review_overlap')
        return 'review';
    if (outcome === 'allowance:not_eligible:before_offer' || outcome === 'allowance:not_eligible:zero_value' || outcome === 'allowance:not_eligible:proration_only')
        return 'next_renewal';
    if (outcome.startsWith('allowance:not_eligible:'))
        return 'unsupported_price';
    return 'checking';
}
export function includedPreparation(input) {
    const { subscription: sub, allowance: a, now } = input;
    const checkedAt = input.check?.at ?? null;
    const out = (state, nextAt = null) => ({ state, nextAt, checkedAt });
    if (!findOffer(input.offerVersion))
        return out('none');
    const t = now.getTime();
    if (a && a.status === 'active' && a.periodStart.getTime() <= t && t < a.periodEnd.getTime())
        return out('live');
    if (sub.status !== 'active' && sub.status !== 'trialing' && sub.status !== 'past_due')
        return out('none');
    const kind = subscriptionKind(sub);
    if (kind === 'legacy_plan' || kind === 'unknown')
        return out('unsupported_price');
    if (input.pendingAllowance)
        return out('scheduled', input.pendingAllowance.periodStart);
    if (sub.status === 'past_due')
        return out('awaiting_payment');
    // A verdict from before the last allowance ended is about an older period.
    const check = input.check && (!a || input.check.at.getTime() >= a.periodEnd.getTime()) ? input.check : null;
    if (!check)
        return out('checking');
    const state = fromOutcome(check.outcome);
    return out(state, state === 'next_renewal' ? (sub.currentPeriodEnd ?? null) : null);
}
//# sourceMappingURL=preparation.js.map